// src/components/admin/BetaUserManager.js
import React, { useState, useEffect } from 'react';
import { auth, db, functions } from '../../firebase';
import { collection, getDocs, query, orderBy, doc, setDoc, serverTimestamp, updateDoc, where, deleteField, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

function BetaUserManager() {
  const [email, setEmail] = useState('');
  const [tier, setTier] = useState('lifetime');
  const [loading, setLoading] = useState(false);
  const [betaUsers, setBetaUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Fix subscription states
  const [fixUserEmail, setFixUserEmail] = useState('');
  const [fixUserPriceId, setFixUserPriceId] = useState('');
  const [fixSubscriptionLoading, setFixSubscriptionLoading] = useState(false);
  
  // Direct subscription update (without Stripe integration)
  const [directUpdateEmail, setDirectUpdateEmail] = useState('');
  const [directUpdateTier, setDirectUpdateTier] = useState('sixMonth');
  const [directUpdateLoading, setDirectUpdateLoading] = useState(false);
  
  // Price IDs mapping for both test and production environments
  const priceIds = {
    test: {
      sixMonth: 'price_1QlfMQCWY7YPUq7bqYk9SQP7',
      lifetime: 'price_1QirApCWY7YPUq7bYE8wXyFu',
    },
    production: {
      sixMonth: 'price_1R1BKdCWY7YPUq7bqSKXpj0C',
      lifetime: 'price_1R1BKkCWY7YPUq7bKaheTCrz',
    }
  };

  // Load beta users
  useEffect(() => {
    fetchBetaUsers();
  }, []);

  // Fetch beta users from Firestore
  const fetchBetaUsers = async () => {
    setLoadingUsers(true);
    try {
      const betaUsersRef = collection(db, 'beta_users');
      const q = query(betaUsersRef, orderBy('added_at', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const users = [];
      querySnapshot.forEach((doc) => {
        users.push({
          id: doc.id,
          ...doc.data(),
          added_at: doc.data().added_at?.toDate().toLocaleString() || 'N/A'
        });
      });
      
      setBetaUsers(users);
    } catch (error) {
      console.error('Error fetching beta users:', error);
      toast.error('Failed to load beta users');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Add beta user directly to Firestore
  const addBetaUser = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }
    
    setLoading(true);
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const currentUser = auth.currentUser;
      
      // Create a document in the beta_users collection with the email as the ID
      const betaUserRef = doc(db, 'beta_users', normalizedEmail);
      
      await setDoc(betaUserRef, {
        email: normalizedEmail,
        added_at: serverTimestamp(),
        added_by: currentUser ? currentUser.uid : 'admin',
        tier: tier,
        active: true
      });
      
      toast.success(`Added ${email} as a beta user with ${tier} tier`);
      setEmail('');
      // Refresh the list
      fetchBetaUsers();
    } catch (error) {
      console.error('Error adding beta user:', error);
      toast.error(error.message || 'Failed to add beta user');
    } finally {
      setLoading(false);
    }
  };

  // Handle fix user subscription with Firebase Function (currently disabled due to deployment issues)
  const handleFixUserSubscriptionWithFunction = async (e) => {
    e.preventDefault();
    
    if (!fixUserEmail.trim() || !fixUserPriceId) {
      toast.error('Both email and price ID are required');
      return;
    }
    
    setFixSubscriptionLoading(true);
    try {
      const fixUserSubscription = httpsCallable(functions, 'fixUserSubscription');
      const result = await fixUserSubscription({
        userEmail: fixUserEmail,
        priceId: fixUserPriceId
      });
      
      console.log('Fix subscription result:', result.data);
      toast.success(`Fixed subscription for ${fixUserEmail}`);
      setFixUserEmail('');
      setFixUserPriceId('');
    } catch (error) {
      console.error('Error fixing user subscription:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setFixSubscriptionLoading(false);
    }
  };
  
  // Direct update user subscription function (no Stripe integration)
  const handleDirectSubscriptionUpdate = async (e) => {
    e.preventDefault();
    
    if (!directUpdateEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    
    setDirectUpdateLoading(true);
    try {
      // Step 1: Find the user by email
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', directUpdateEmail.toLowerCase().trim()));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        toast.error(`No user found with email: ${directUpdateEmail}`);
        return;
      }
      
      // Get the first matching user
      const userDoc = querySnapshot.docs[0];
      const userId = userDoc.id;
      const userData = userDoc.data();
      
      console.log(`Found user: ${userId} (${userData.email})`);
      console.log(`Current subscription tier: ${userData.subscriptionTier || 'none'}`);
      
      // Step 2: Update the user's subscription in Firestore
      const userRef = doc(db, 'users', userId);
      
      if (directUpdateTier === 'lifetime') {
        await updateDoc(userRef, {
          subscriptionTier: 'lifetime',
          purchaseDate: serverTimestamp(),
          expirationDate: deleteField(),
        });
        console.log(`Set user ${userId} to lifetime plan.`);
      } else if (directUpdateTier === 'sixMonth') {
        const sixMonthsFromNow = new Date();
        sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
        
        await updateDoc(userRef, {
          subscriptionTier: 'sixMonth',
          purchaseDate: serverTimestamp(),
          expirationDate: sixMonthsFromNow,
        });
        console.log(`Set user ${userId} to sixMonth plan, expiring around ${sixMonthsFromNow.toLocaleDateString()}.`);
      } else if (directUpdateTier === 'none') {
        await updateDoc(userRef, {
          subscriptionTier: 'none',
          expirationDate: deleteField(),
        });
        console.log(`Removed subscription for user ${userId}.`);
      }
      
      toast.success(`Successfully updated ${directUpdateEmail} to ${directUpdateTier} subscription`);
      setDirectUpdateEmail('');
      
    } catch (error) {
      console.error('Error updating user subscription:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setDirectUpdateLoading(false);
    }
  };

  // Handle fix user subscription directly in Firestore (bypass Cloud Functions)
  const handleFixUserSubscription = async (e) => {
    e.preventDefault();
    
    if (!fixUserEmail.trim() || !fixUserPriceId) {
      toast.error('Both email and price ID are required');
      return;
    }
    
    setFixSubscriptionLoading(true);
    try {
      // Step 1: Find the user by email
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', fixUserEmail.toLowerCase().trim()));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        toast.error(`No user found with email: ${fixUserEmail}`);
        return;
      }
      
      // Get the first matching user
      const userDoc = querySnapshot.docs[0];
      const userId = userDoc.id;
      const userData = userDoc.data();
      
      console.log(`Found user: ${userId} (${userData.email})`);
      console.log(`Current subscription tier: ${userData.subscriptionTier || 'none'}`);
      
      // Step 2: Determine which subscription tier to apply based on price ID
      let tierKey = null;
      
      // Check which plan this price ID corresponds to
      if (fixUserPriceId === priceIds.test.sixMonth || fixUserPriceId === priceIds.production.sixMonth) {
        tierKey = 'sixMonth';
      } else if (fixUserPriceId === priceIds.test.lifetime || fixUserPriceId === priceIds.production.lifetime) {
        tierKey = 'lifetime';
      } else {
        toast.error(`Unrecognized price ID: ${fixUserPriceId}`);
        return;
      }
      
      // Step 3: Update the user's subscription in Firestore
      const userRef = doc(db, 'users', userId);
      
      if (tierKey === 'lifetime') {
        await updateDoc(userRef, {
          subscriptionTier: 'lifetime',
          purchaseDate: serverTimestamp(),
          expirationDate: deleteField(),
        });
        console.log(`Set user ${userId} to lifetime plan.`);
      } else if (tierKey === 'sixMonth') {
        const sixMonthsFromNow = new Date();
        sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
        
        await updateDoc(userRef, {
          subscriptionTier: 'sixMonth',
          purchaseDate: serverTimestamp(),
          expirationDate: sixMonthsFromNow,
        });
        console.log(`Set user ${userId} to sixMonth plan, expiring around ${sixMonthsFromNow.toLocaleDateString()}.`);
      }
      
      toast.success(`Successfully updated ${fixUserEmail} to ${tierKey} subscription`);
      setFixUserEmail('');
      setFixUserPriceId('');
      
    } catch (error) {
      console.error('Error fixing user subscription:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setFixSubscriptionLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-2xl font-bold mb-6">Beta User Management</h2>
      
      {/* Add Beta User Form */}
      <form onSubmit={addBetaUser} className="mb-8">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="user@example.com"
              required
            />
          </div>
          
          <div className="w-40">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Access Tier
            </label>
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
            >
              <option value="lifetime">Lifetime</option>
              <option value="sixMonth">Six Month</option>
            </select>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Adding...' : 'Add Beta User'}
          </button>
        </div>
      </form>
      
      {/* Direct Subscription Update Form */}
      <h3 className="text-xl font-semibold mb-4">Direct Subscription Update</h3>
      <form onSubmit={handleDirectSubscriptionUpdate} className="mb-8 bg-blue-50 p-4 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-700 mb-4">
          Use this form to directly update a user's subscription tier in Firestore. This is the simplest way to fix subscription issues.
        </p>
        
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Email
            </label>
            <input
              type="email"
              value={directUpdateEmail}
              onChange={(e) => setDirectUpdateEmail(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="user@example.com"
              required
            />
          </div>
          
          <div className="w-40">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Tier
            </label>
            <select
              value={directUpdateTier}
              onChange={(e) => setDirectUpdateTier(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
            >
              <option value="lifetime">Lifetime</option>
              <option value="sixMonth">Six Month</option>
              <option value="none">None</option>
            </select>
          </div>
          
          <button
            type="submit"
            disabled={directUpdateLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {directUpdateLoading ? 'Updating...' : 'Update Subscription'}
          </button>
        </div>
      </form>
      
      {/* Fix User Subscription Form */}
      <h3 className="text-xl font-semibold mb-4">Fix Stripe-Based Subscription</h3>
      <form onSubmit={handleFixUserSubscription} className="mb-8 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <p className="text-sm text-yellow-700 mb-4">
          Use this form to manually fix a user's subscription when they completed payment in Stripe but their subscription didn't update in the system.
        </p>
        
        <div className="bg-green-50 border border-green-200 rounded p-3 mb-4">
          <p className="text-sm text-green-800">
            <strong>Direct Update:</strong> This tool will directly update the user's subscription tier in Firestore, bypassing the Cloud Functions.
          </p>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Email
            </label>
            <input
              type="email"
              value={fixUserEmail}
              onChange={(e) => setFixUserEmail(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="user@example.com"
              required
            />
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price ID (from Stripe)
            </label>
            <select
              value={fixUserPriceId}
              onChange={(e) => setFixUserPriceId(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
              required
            >
              <option value="">Select a price ID...</option>
              <option value={priceIds.production.sixMonth}>Six Month (Production)</option>
              <option value={priceIds.production.lifetime}>Lifetime (Production)</option>
              <option value={priceIds.test.sixMonth}>Six Month (Test)</option>
              <option value={priceIds.test.lifetime}>Lifetime (Test)</option>
            </select>
          </div>
          
          <button
            type="submit"
            disabled={fixSubscriptionLoading}
            className="bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
          >
            {fixSubscriptionLoading ? 'Fixing...' : 'Fix Subscription'}
          </button>
        </div>
      </form>
      
      {/* Beta Users List */}
      <h3 className="text-xl font-semibold mb-4">Beta Users</h3>
      
      {loadingUsers ? (
        <p className="text-gray-500">Loading beta users...</p>
      ) : betaUsers.length === 0 ? (
        <p className="text-gray-500">No beta users found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tier
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Added
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {betaUsers.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.tier === 'lifetime' ? 'Lifetime' : 'Six Month'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.added_at}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default BetaUserManager;